import type { KeyboardEvent } from 'react';
import React from 'react';

import { isFirefox } from '../../../utils/devices/device';
import BaseInput from '../BaseInput/BaseInput';
import type { BaseInputProps } from '../types';
import { Size, Status } from '../types';

type OmittedProps = 'RightIcon' | 'onRightIconClick';

export interface InputProps extends Omit<BaseInputProps, OmittedProps> {
  /**
   * Input type
   */
  type?: string;
  /**
   * Function for input action
   */
  onEnter?: (event: KeyboardEvent<HTMLInputElement>) => void;

  elementRef?: React.MutableRefObject<HTMLInputElement | null>;
}

const numericalInputRegex = /^(\s*|\d+|\.)$/;

const Input = ({
  autofocus = false,
  blurOnEnter = true,
  dataCy,
  disabled = false,
  id,
  info,
  maxLength,
  minLength,
  name,
  onBlur,
  onChange,
  onEnter,
  onFocus,
  placeholder,
  readOnly = false,
  elementRef,
  size = Size.MEDIUM,
  status = Status.DEFAULT,
  type = 'text',
  value,
  forceFontSize,
  ...rest
}: InputProps) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onEnter?.(event);
    } else if (type === 'number' && isFirefox) {
      if (event.key === 'Backspace' || event.key === 'Delete') {
        return;
      }

      if (!numericalInputRegex.test(event.key)) {
        event.preventDefault();
      }
    }
  };

  return (
    <BaseInput
      aria-label={rest['aria-label']}
      autofocus={autofocus}
      blurOnEnter={blurOnEnter}
      dataCy={dataCy}
      disabled={disabled}
      id={id}
      info={info}
      maxLength={maxLength}
      minLength={minLength}
      name={name}
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      readOnly={readOnly}
      ref={elementRef}
      size={size}
      status={status}
      type={type}
      value={value}
      forceFontSize={forceFontSize}
    />
  );
};

Input.Size = Size;
Input.Status = Status;

export default Input;
